import React from 'react';
import PropTypes from 'prop-types';
import { useViewMode } from '../../hooks/useViewMode.jsx';
import { useTranslation } from '../../utils/translations';
import LanguageToggle from '../Common/LanguageToggle';

import Header from '../Layout/Header';
import SettingsPanel from './SettingsPanel';
import StudentGroupedView from './StudentGroupedView';
import ActivityGroupedView from './ActivityGroupedView';
import DateGroupedView from './DateGroupedView';
import './DataView.css';

const DataView = ({ phoneNumber, familyData, onBack }) => {
  const { viewMode } = useViewMode();
  const { t } = useTranslation();

  if (!familyData) {
    return (
      <div className="data-view-container">
        <Header />
        <main className="data-view-main">
          <p>{t('main.noFamilyData')}</p>
        </main>
      </div>
    );
  }

  const { familyId, parentName1, parentName2, children, lastUpdated } = familyData;
  // Transform mock data into format expected by grouped views
  const transformDataForViews = (familyData) => {
    const students = familyData.children.map(child => ({
      id: child.childId,
      name: child.studentName,
      birthdate: child.birthdate,
      activities: [
        {
          id: 'sundaySchool',
          status: child.sundaySchool ? 'completed' : 'pending',
          details: child.sundaySchool
        },
        {
          id: 'russianSchool', 
          status: child.russianSchool ? 'completed' : 'pending',
          details: child.russianSchool
        },
        {
          id: 'childrensChoir',
          status: child.childrensChoir ? 'completed' : 'pending',
          details: child.childrensChoir
        }
      ]
    }));

    const activities = [
      {
        id: 'sundaySchool',
        name: 'Sunday School',
        day: familyData.children[0]?.sundaySchool?.day || 'Wednesday',
        icon: 'FaBook'
      },
      {
        id: 'russianSchool',
        name: 'Russian School',
        day: familyData.children[0]?.russianSchool?.day || 'Saturday',
        icon: 'FaGlobeEurope'
      },
      {
        id: 'childrensChoir',
        name: "Children's Choir",
        day: familyData.children[0]?.childrensChoir?.day || 'Monday',
        icon: 'FaMusic'
      }
    ];

    return { students, activities };
  };

  const { students, activities } = transformDataForViews(familyData);

  return (
    <div className="data-view-container">
      <Header />
      <main className="data-view-main" id="data-to-export">
        <div className="family-header-card">
          <div className="header-with-logo">
            <img 
              src="/aos-logo.png" 
              alt="Ark of Salvation Logo" 
              className="family-logo desktop-logo"
            />
            <img 
              src="/logo.svg" 
              alt="Ark of Salvation Logo" 
              className="family-logo mobile-logo"
            />
            <div className="family-info">
              <div className="parent-names">
                <span className="parent-name">{parentName1}</span>
                {parentName2 && (
                  <>
                    <span className="name-connector">&</span>
                    <span className="parent-name">{parentName2}</span>
                  </>
                )}
              </div>
              <div className="family-subtitle">Family</div>
            </div>
            <div className="family-language-toggle">
              <LanguageToggle />
            </div>
          </div>
        </div>
        {lastUpdated && (
          <div className="last-updated-banner">
            <small className="last-updated-text">
              {t('main.lastUpdated')}: {new Date(lastUpdated).toLocaleDateString()}
            </small>
          </div>
        )}
        {viewMode === 'student' ? (
          <StudentGroupedView students={students} activities={activities} />
        ) : viewMode === 'activity' ? (
          <ActivityGroupedView students={students} activities={activities} />
        ) : (
          <DateGroupedView students={students} activities={activities} />
        )}
      </main>
      <SettingsPanel onSignOut={onBack} data={{ familyData }} />
    </div>
  );
};

DataView.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  familyData: PropTypes.object,
  onBack: PropTypes.func,
};

export default DataView;
